import { Upload } from "tus-js-client";
import { BackendError } from "../errors";

export function uploadResumable(input: {
  url: string; token: string; ownerId: string; attachmentId: string; storagePath: string;
  mediaType: string; file: Blob; signal?: AbortSignal; onProgress?: (progress: number) => void;
}): Promise<void> {
  const endpoint = new URL(input.url);
  if (endpoint.hostname.endsWith(".supabase.co")) endpoint.hostname = endpoint.hostname.replace(".supabase.co", ".storage.supabase.co");
  endpoint.pathname = "/storage/v1/upload/resumable";
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      input.signal?.removeEventListener("abort", abort);
      if (error) reject(error); else resolve();
    };
    const upload = new Upload(input.file, {
      endpoint: endpoint.href,
      chunkSize: 6 * 1024 * 1024,
      retryDelays: [0, 1000, 3000, 5000],
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      headers: { authorization: `Bearer ${input.token}`, "x-upsert": "false" },
      metadata: { bucketName: "task-attachments", objectName: input.storagePath, contentType: input.mediaType, cacheControl: "0" },
      fingerprint: async () => [endpoint.origin, input.ownerId, input.attachmentId, input.storagePath, input.file.size].join(":"),
      onProgress: (sent, total) => { if (!settled) input.onProgress?.(total ? sent / total : 0); },
      onError: () => finish(new BackendError("unknown", "The file could not be uploaded. Retry to continue.", { retryable: true })),
      onSuccess: () => finish(),
    });
    const abort = () => {
      void upload.abort().catch(() => undefined);
      finish(new BackendError("unknown", "The upload was cancelled.", { retryable: true }));
    };
    input.signal?.addEventListener("abort", abort, { once: true });
    if (input.signal?.aborted) { abort(); return; }
    upload.findPreviousUploads().then((previous) => {
      if (settled) return;
      if (previous[0]) upload.resumeFromPreviousUpload(previous[0]);
      upload.start();
    }).catch(() => { if (!settled) upload.start(); });
  });
}
